import { useCallback, useEffect, useRef } from 'react'
import { createLogger } from '@/lib/logger'
import { getNotificationRoute } from '@/lib/notifications/notification-route'
import { isTauri } from '@/lib/utils/tauri'
import {
  browserNotificationService,
  BrowserNotificationService,
} from '@/services/browserNotificationService'
import { useBennyStore } from '@/store/benny.store'
import { useNotificationStore } from '@/store/notifications'
import { useOrganizationStore } from '@/store/organization.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'

import {
  AppPreferences,
  BrowserNotificationPreferences,
  DesktopNotificationSettings,
  isBrowserNotificationPreferences,
  NotificationType,
  PreferenceContextEnum,
} from '@scrambl/shared'

const logger = createLogger('hooks/useBrowserNotifications')

type NotificationHandler = (notification: any) => void

// Ensure we only register one window listener to avoid duplicate native notifications.
const notificationHandlers = new Set<NotificationHandler>()
let notificationListenerAttached = false

const getActiveNotificationHandler = (): NotificationHandler | null => {
  let handler: NotificationHandler | null = null
  for (const entry of notificationHandlers) {
    handler = entry
  }
  return handler
}

const sharedNotificationListener = (event: Event) => {
  const handler = getActiveNotificationHandler()
  if (!handler) return
  const customEvent = event as CustomEvent
  handler(customEvent.detail)
}

export const useBrowserNotifications = () => {
  const { getPreference, savePreference } = useUserPreferencesStore()
  // Track if we've already run the initialization effect to prevent duplicate API calls
  const hasInitializedRef = useRef(false)
  const { handleNotificationCreated } = useNotificationStore()
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )

  // Get current browser notification preferences (reactive)
  const getBrowserNotificationPreferences =
    useCallback((): BrowserNotificationPreferences => {
      const preference = getPreference(PreferenceContextEnum.NOTIFICATIONS)

      if (
        preference?.preferences &&
        isBrowserNotificationPreferences(preference.preferences)
      ) {
        return preference.preferences
      }

      return BrowserNotificationService.getDefaultPreferences()
    }, [getPreference])

  // Reactive preferences that will trigger re-renders
  const preferences = getBrowserNotificationPreferences()

  // Save browser notification preferences
  const saveBrowserNotificationPreferences = useCallback(
    async (preferences: BrowserNotificationPreferences): Promise<void> => {
      try {
        await savePreference(PreferenceContextEnum.NOTIFICATIONS, preferences)

        // Update the service with new preferences
        browserNotificationService.updatePreferences(preferences)
      } catch (error) {
        logger.error('Failed to save browser notification preferences:', error)
        throw error
      }
    },
    [savePreference],
  )

  // Request browser notification permission
  const requestPermission =
    useCallback(async (): Promise<NotificationPermission> => {
      const permission = await browserNotificationService.requestPermission()

      // Update preferences to reflect permission state
      const currentPrefs = preferences
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        permissionRequested: true,
        permissionStatus: permission,
        enabled: permission === 'granted' ? currentPrefs.enabled : false,
      }

      await saveBrowserNotificationPreferences(updatedPrefs)

      return permission
    }, [preferences, saveBrowserNotificationPreferences])

  // Enable browser notifications
  const enableBrowserNotifications = useCallback(async (): Promise<boolean> => {
    const currentPrefs = preferences

    // Check if permission is already granted
    if (currentPrefs.permissionStatus === 'granted') {
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        enabled: true,
        userDisabled: false,
      }

      await saveBrowserNotificationPreferences(updatedPrefs)
      return true
    }

    // Request permission if not already requested
    const permission = await requestPermission()

    if (permission === 'granted') {
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        enabled: true,
        permissionStatus: permission,
        permissionRequested: true,
        userDisabled: false,
      }

      await saveBrowserNotificationPreferences(updatedPrefs)
      return true
    }

    return false
  }, [preferences, requestPermission, saveBrowserNotificationPreferences])

  // Disable browser notifications
  const disableBrowserNotifications = useCallback(async (): Promise<void> => {
    const currentPrefs = preferences
    const updatedPrefs: BrowserNotificationPreferences = {
      ...currentPrefs,
      enabled: false,
      userDisabled: true,
    }

    await saveBrowserNotificationPreferences(updatedPrefs)
  }, [preferences, saveBrowserNotificationPreferences])

  // Update specific notification type settings
  const updateNotificationTypes = useCallback(
    async (
      enabledTypes: Partial<
        NonNullable<BrowserNotificationPreferences['enabledTypes']>
      >,
    ): Promise<void> => {
      const currentPrefs = preferences
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        enabledTypes: {
          ...currentPrefs.enabledTypes,
          ...enabledTypes,
        },
      }

      await saveBrowserNotificationPreferences(updatedPrefs)
    },
    [preferences, saveBrowserNotificationPreferences],
  )

  // Update display settings
  const updateDisplaySettings = useCallback(
    async (
      settings: Pick<
        BrowserNotificationPreferences,
        | 'showWhenTabActive'
        | 'showWhenTabInactive'
        | 'playSound'
        | 'autoDismissMs'
      >,
    ): Promise<void> => {
      const currentPrefs = preferences
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        ...settings,
      }

      await saveBrowserNotificationPreferences(updatedPrefs)
    },
    [preferences, saveBrowserNotificationPreferences],
  )

  // Update desktop-specific settings (Tauri app only)
  const updateDesktopSettings = useCallback(
    async (
      desktopSettings: Partial<DesktopNotificationSettings>,
    ): Promise<void> => {
      const currentPrefs = preferences
      const updatedPrefs: BrowserNotificationPreferences = {
        ...currentPrefs,
        desktopSettings: {
          ...currentPrefs.desktopSettings,
          ...desktopSettings,
        },
      }

      await saveBrowserNotificationPreferences(updatedPrefs)
    },
    [preferences, saveBrowserNotificationPreferences],
  )

  // Update badge count (Tauri app only)
  const updateBadgeCount = useCallback(
    async (count: number): Promise<void> => {
      if (!isTauri()) return

      const desktopSettings = preferences.desktopSettings
      if (desktopSettings?.showBadgeCount !== false) {
        await browserNotificationService.setBadgeCount(count)
      }
    },
    [preferences.desktopSettings],
  )

  // Clear badge count (Tauri app only)
  const clearBadgeCount = useCallback(async (): Promise<void> => {
    if (!isTauri()) return
    await browserNotificationService.clearBadgeCount()
  }, [])

  // Handle incoming notifications and show browser notifications
  const handleIncomingNotification = useCallback(
    (notification: any) => {
      const currentPrefs = preferences

      if (!currentPrefs.enabled || !browserNotificationService.isSupported()) {
        return
      }

      // When the floating Benny companion is delivering notifications
      // (speech bubbles), skip the native system notification so the user
      // isn't notified twice. BennyCompanionController forwards the same
      // notification to the companion window.
      if (isTauri() && useBennyStore.getState().companionActive) {
        const appPrefs = getUserAppPreference(
          useUserPreferencesStore.getState().preferences,
        )?.preferences as AppPreferences | undefined
        if (appPrefs?.bennyNotificationsEnabled === true) {
          return
        }
      }

      const notificationData = (notification?.data ?? {}) as Record<
        string,
        unknown
      >
      const organizationSlug = currentOrganization?.slug
      const taskIdentifierRaw =
        notificationData.taskNumber ??
        notificationData.taskId ??
        notification?.entityId
      const taskIdentifier = taskIdentifierRaw
        ? String(taskIdentifierRaw)
        : undefined
      const projectIdRaw =
        notificationData.projectId ?? notificationData.moduleId
      const projectId = projectIdRaw ? String(projectIdRaw) : undefined
      const threadIdRaw =
        notificationData.threadId ??
        (notification?.entityType === 'THREAD'
          ? notification?.entityId
          : undefined)
      const threadId = threadIdRaw ? String(threadIdRaw) : undefined
      const noteIdRaw =
        notificationData.noteId ??
        (notification?.entityType === 'NOTE'
          ? notification?.entityId
          : undefined)
      const noteId = noteIdRaw ? String(noteIdRaw) : undefined
      const threadType =
        notificationData.threadType === 'DIRECT_MESSAGE' ||
        notificationData.threadType === 'DISCUSSION'
          ? (notificationData.threadType as 'DIRECT_MESSAGE' | 'DISCUSSION')
          : notificationData.isDirectMessage
            ? 'DIRECT_MESSAGE'
            : undefined
      const bodyText =
        notification.message ||
        notification.body ||
        notification.title ||
        'You have a new notification'
      const notificationRoute = getNotificationRoute(
        organizationSlug,
        notification,
      )

      const buildEntityUrl = () => {
        if (notificationRoute) return notificationRoute
        if (!organizationSlug) return undefined
        switch (notification?.entityType) {
          case 'TASK':
            return taskIdentifier
              ? `/${organizationSlug}/tasks/${taskIdentifier}`
              : undefined
          case 'PROJECT':
            return notification?.entityId
              ? `/${organizationSlug}/projects/${notification.entityId}`
              : undefined
          case 'THREAD': {
            if (!threadId) return undefined
            return `/${organizationSlug}/threads?tab=discussions&thread=${threadId}`
          }
          case 'NOTE':
            return noteId ? `/${organizationSlug}/notes/${noteId}` : undefined
          default:
            return undefined
        }
      }
      const entityUrl = buildEntityUrl()

      // Determine notification type and show appropriate browser notification
      switch (notification.type) {
        case NotificationType.TASK_ASSIGNED:
          browserNotificationService.showAssignmentNotification(
            'Task Assigned',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
          )
          break

        case NotificationType.TASK_STATUS_CHANGED:
          browserNotificationService.showTaskNotification(
            'Task Status Changed',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
          )
          break

        case NotificationType.TASK_DUE_SOON:
          browserNotificationService.showDeadlineNotification(
            'Task Due Soon',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
          )
          break

        case NotificationType.TASK_OVERDUE:
          browserNotificationService.showDeadlineNotification(
            'Task Overdue',
            bodyText,
            taskIdentifier,
            projectId,
            organizationSlug,
          )
          break

        case NotificationType.TASK_COMMENT_ADDED:
        case NotificationType.COMMENT_REPLY:
          if (threadId && notification?.entityType === 'THREAD') {
            browserNotificationService.showThreadMessageNotification(
              notification.title || 'New comment',
              bodyText,
              threadId,
              organizationSlug,
              threadType,
            )
          } else {
            browserNotificationService.showCommentNotification(
              'New Comment',
              bodyText,
              taskIdentifier,
              threadId,
              organizationSlug,
              entityUrl,
            )
          }
          break

        case NotificationType.TASK_MENTION:
        case NotificationType.COMMENT_MENTION:
        case NotificationType.NOTE_MENTION:
          if (threadId && notification?.entityType === 'THREAD') {
            browserNotificationService.showThreadMessageNotification(
              notification.title || 'Mentioned in a thread',
              bodyText,
              threadId,
              organizationSlug,
              threadType,
            )
          } else {
            browserNotificationService.showMentionNotification(
              'You were mentioned',
              bodyText,
              taskIdentifier,
              threadId,
              organizationSlug,
              entityUrl,
            )
          }
          break

        default:
          browserNotificationService.showSystemNotification(
            notification.title || 'New Notification',
            bodyText,
            entityUrl,
          )
          break
      }
    },
    [currentOrganization?.slug, preferences],
  )

  // Update service preferences when preferences change (local only, no API call)
  useEffect(() => {
    browserNotificationService.updatePreferences(preferences)
  }, [preferences])

  // Consolidated one-time initialization effect for permission sync and auto-enable
  // This combines both operations into a single save to avoid duplicate API calls
  useEffect(() => {
    // Only run once per mount
    if (hasInitializedRef.current) return
    if (!browserNotificationService.isSupported()) return
    if (!currentOrganization?.id) return

    hasInitializedRef.current = true
    const currentStatus = browserNotificationService.getPermissionStatus()

    // Build updated preferences combining permission sync and auto-enable logic
    const updatedPrefs: BrowserNotificationPreferences = { ...preferences }
    let needsSave = false

    // Sync permission status if different
    if (preferences.permissionStatus !== currentStatus) {
      updatedPrefs.permissionStatus = currentStatus
      needsSave = true
    }

    // Auto-enable logic (only if user hasn't disabled)
    if (!preferences.userDisabled && !preferences.enabled) {
      if (currentStatus === 'granted') {
        updatedPrefs.enabled = true
        updatedPrefs.permissionRequested = true
        needsSave = true
      } else if (
        currentStatus === 'default' &&
        !preferences.permissionRequested
      ) {
        // Request permission and save after response
        browserNotificationService
          .requestPermission()
          .then(permission => {
            const finalPrefs: BrowserNotificationPreferences = {
              ...updatedPrefs,
              enabled: permission === 'granted',
              permissionStatus: permission,
              permissionRequested: true,
            }
            saveBrowserNotificationPreferences(finalPrefs).catch(logger.error)
          })
          .catch(logger.error)
        return // Early return - save will happen in the promise
      }
    }

    // Save only if changes are needed
    if (needsSave) {
      saveBrowserNotificationPreferences(updatedPrefs).catch(logger.error)
    }
  }, [currentOrganization?.id, preferences, saveBrowserNotificationPreferences])

  // Listen for notification created events from the store
  useEffect(() => {
    notificationHandlers.add(handleIncomingNotification)
    if (!notificationListenerAttached) {
      window.addEventListener(
        'scrambl:notification-created',
        sharedNotificationListener as EventListener,
      )
      notificationListenerAttached = true
    }

    return () => {
      notificationHandlers.delete(handleIncomingNotification)
      if (notificationHandlers.size === 0 && notificationListenerAttached) {
        window.removeEventListener(
          'scrambl:notification-created',
          sharedNotificationListener as EventListener,
        )
        notificationListenerAttached = false
      }
    }
  }, [handleIncomingNotification])

  return {
    // State
    preferences,
    isSupported: browserNotificationService.isSupported(),
    permissionStatus: browserNotificationService.getPermissionStatus(),
    isDesktopApp: isTauri(),

    // Actions
    requestPermission,
    enableBrowserNotifications,
    disableBrowserNotifications,
    updateNotificationTypes,
    updateDisplaySettings,
    updateDesktopSettings,
    saveBrowserNotificationPreferences,

    // Badge count (desktop only)
    updateBadgeCount,
    clearBadgeCount,

    // Handlers
    handleIncomingNotification,
  }
}
